import { useState, useEffect, useCallback } from 'react';
import { wordsOfWisdomAPI } from './services/api';
import './USA/GmailWordsOfWisdom.css';

// Branding assets — verified accessible URLs
const WOW_BANNER_URL = 'https://yugalkunj.org/images/wordofwisdom.png';
const FALLBACK_BANNER_URL = 'https://yugalkunj.org/images/wordofwisdom.png';
const FACEBOOK_URL = 'https://www.facebook.com/yugalkunj';
const YOUTUBE_URL = 'https://www.youtube.com/yugalkunj';
const INSTAGRAM_URL = 'https://www.instagram.com/yugalkunj';
const CONTACT_EMAIL = 's_didi@radhamadhavsociety.org';

const WordofWisdomeCard = () => {
    const [emails, setEmails] = useState([]);
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [error, setError] = useState(null);

    // Fetch all Words of Wisdom emails
    const loadEmails = useCallback(async () => {
        try {
            setLoading(true);
            const result = await wordsOfWisdomAPI.getAll();
            if (result.success !== false) {
                setEmails(Array.isArray(result) ? result : result.data || []);
            } else {
                setError(result.message || 'Failed to load Words of Wisdom');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadEmails();
    }, [loadEmails]);

    // Load detail of a single email
    const loadEmailDetail = useCallback(async (id) => {
        try {
            setLoadingDetail(true);
            const result = await wordsOfWisdomAPI.getById(id);
            const emailData = Array.isArray(result) ? result[0] : (result.data || result);
            if (emailData) {
                setSelectedEmail(emailData);
            } else {
                setError('Email not found');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoadingDetail(false);
        }
    }, []);

    // Handle image load errors by switching to a known fallback
    const handleImageError = (e) => {
        if (e.target.src !== FALLBACK_BANNER_URL) {
            e.target.src = FALLBACK_BANNER_URL;
        }
    };

    // Format date for display (long form)
    const formatDate = (dateStr) => {
        try {
            const d = new Date(dateStr);
            return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        } catch {
            return dateStr;
        }
    };

    // Format date to match email template exactly (Aug. 7, 2026)
    const formatEmailDate = (dateStr) => {
        try {
            const d = new Date(dateStr);
            const month = d.toLocaleDateString('en-US', { month: 'short' });
            const day = d.getDate();
            const year = d.getFullYear();
            return `${month} ${day}, ${year}`;
        } catch {
            return dateStr;
        }
    };

    // Sanitize and prepare body content for the email-style template
    const getBodyContent = (email) => {
        if (email?.htmlBody && email.htmlBody.trim().length > 0) {
            return email.htmlBody;
        }
        if (email?.textBody && email.textBody.trim().length > 0) {
            return email.textBody.replace(/\n/g, '<br/>');
        }
        return '<p>No content available.</p>';
    };

    return (
        <div className="wow-public-page">
            <div className="wow-public-container">

                {/* Error Message */}
                {error && (
                    <div className="wow-error">
                        <i className="fas fa-exclamation-circle"></i>
                        <span>{error}</span>
                        <button onClick={() => setError(null)} aria-label="Dismiss">&#10005;</button>
                    </div>
                )}

                {/* Loading state */}
                {loading && (
                    <div className="wow-loading">
                        <div className="wow-loading__spinner"></div>
                        <p>Loading Words of Wisdom...</p>
                    </div>
                )}

                {/* Loading detail spinner */}
                {loadingDetail && (
                    <div className="wow-loading">
                        <div className="wow-loading__spinner"></div>
                        <p>Loading message...</p>
                    </div>
                )}

                {/* Detail view — full email template */}
                {selectedEmail && !loadingDetail && (
                    <div className="wow-email-card">
                        <button
                            className="wow-back-btn"
                            onClick={() => setSelectedEmail(null)}
                            aria-label="Back to messages"
                        >
                            <i className="fas fa-arrow-left"></i> Back to Messages
                        </button>

                        {/* Email header */}
                        <div className="wow-email-header">
                            <div className="wow-email-header__brand">
                                <div className="wow-email-header__text">
                                    <div className="wow-email-header__org">Radha Madhav Society</div>
                                    <div className="wow-email-header__date">{formatEmailDate(selectedEmail.emailDate)}</div>
                                </div>
                            </div>
                        </div>

                        {/* Email hero banner */}
                        <div className="wow-email-hero">
                            <img
                                src={WOW_BANNER_URL}
                                alt="Words of Wisdom"
                                className="wow-email-hero__image"
                                onError={handleImageError}
                            />
                        </div>

                        {/* Subject bar */}
                        <div className="wow-email-subject-bar">
                            <span>{selectedEmail.subject}</span>
                        </div>

                        {/* Body */}
                        <div className="wow-email-body">
                            <div
                                className="wow-email-content"
                                dangerouslySetInnerHTML={{ __html: getBodyContent(selectedEmail) }}
                            />

                            <div className="wow-email-signoff">
                                <p>Yours,</p>
                                <p className="wow-email-signoff__name">Didi Ji</p>
                            </div>

                            <hr className="wow-email-divider" />

                            <div className="wow-email-contact">
                                <p>
                                    <em>Note: You may direct your spiritual questions to me directly at</em>
                                </p>
                                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                            </div>

                            <div className="wow-email-social">
                                <p>Follow us on</p>
                                <div className="wow-email-social__links">
                                    <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                                        <i className="fab fa-facebook-f"></i>
                                    </a>
                                    <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                                        <i className="fab fa-youtube"></i>
                                    </a>
                                    <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                                        <i className="fab fa-instagram"></i>
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="wow-email-footer">
                            <p>&copy; {new Date().getFullYear()} Radha Madhav Society. Yugal Kunj, 2769 Duluth Hwy, Duluth, GA 30096</p>
                        </div>
                    </div>
                )}

                {/* List view */}
                {!loading && !selectedEmail && !loadingDetail && (
                    <>
                        {emails.length === 0 ? (
                            <div className="wow-empty">
                                <div className="wow-empty__icon">
                                    <i className="fas fa-envelope-open"></i>
                                </div>
                                <h5>No Words of Wisdom Available</h5>
                                <p>Check back soon for new "Words of Wisdom" messages from Didi Ji.</p>
                            </div>
                        ) : (
                            <>
                                <div className="wow-list-header">
                                    <div className="wow-list-header__hero">
                                        <img
                                            src={WOW_BANNER_URL}
                                            alt="Words of Wisdom"
                                            className="wow-list-header__hero-image"
                                            onError={handleImageError}
                                        />
                                    </div>
                                    <div className="wow-list-header__brand">
                                        <div>
                                            <div className="wow-list-header__org">Radha Madhav Society</div>
                                            <div className="wow-list-header__subtitle">Words of Wisdom</div>
                                        </div>
                                    </div>
                                    <p className="wow-list-header__desc">
                                        Inspirational messages from Didi Ji. Click any message to read the full reflection.
                                    </p>
                                </div>

                                <div className="wow-list-count">
                                    Showing {emails.length} message{emails.length !== 1 ? 's' : ''}
                                </div>

                                <div className="wow-list-grid">
                                    {emails.map((email) => (
                                        <div
                                            key={email.wordsOfWisdomId}
                                            className="wow-list-card"
                                            onClick={() => loadEmailDetail(email.wordsOfWisdomId)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') loadEmailDetail(email.wordsOfWisdomId); }}
                                        >
                                            <div className="wow-list-card__top">
                                                <span className="wow-list-card__date">
                                                    <i className="far fa-calendar"></i> {formatDate(email.emailDate)}
                                                </span>
                                                <span className="wow-list-card__badge">
                                                    <i className="fas fa-om"></i>
                                                </span>
                                            </div>

                                            <h5 className="wow-list-card__subject">{email.subject}</h5>

                                            <p className="wow-list-card__from">
                                                <i className="fas fa-user-circle"></i>
                                                {email.senderName || 'Radha Madhav Society'}
                                            </p>

                                            <p className="wow-list-card__snippet">
                                                {email.snippet || 'Click to read this inspirational message.'}
                                            </p>

                                            <div className="wow-list-card__action">
                                                <span>Read More <i className="fas fa-arrow-right"></i></span>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="wow-list-footer">
                                    <p>Yours,</p>
                                    <p className="wow-list-footer__name">Didi Ji</p>
                                    <p className="wow-list-footer__note">
                                        Note: You may direct your spiritual questions to me directly at{' '}
                                        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                                    </p>
                                </div>
                            </>
                        )}
                    </>
                )}

            </div>
        </div>
    );
};

export default WordofWisdomeCard;
